'use client';

import { useCallback } from 'react';
import { Keypair, PublicKey } from '@solana/web3.js';
import {
	BaseMessageSignerWalletAdapter,
	WalletReadyState,
} from '@solana/wallet-adapter-base';
import {
	IWallet,
	TokenFaucet,
	VELOCITY_PROGRAM_ID,
} from '@velocity-exchange/sdk';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useFetchAndSubscribeSubAccounts } from '../user/useFetchAndSubscribeSubaccounts/useFetchAndSubscribeSubaccounts';
import { useSyncDeletedSubaccountsPubkey } from '../user/useFetchAndSubscribeSubaccounts/useSyncDeletedSubaccounts';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';

const useEmulateAccount = () => {
	const driftClient = useDriftClient();
	const walletState = useDriftStore((s) => s.wallet);
	const connection = useDriftStore((s) => s.connection.current);

	const setDriftStore = useDriftStore((s) => s.set);
	const resetAccountsState = useDriftAccountsStore((s) => s.resetAccountsState);

	const { fetchAndSubscribeSubAccounts } = useFetchAndSubscribeSubAccounts();
	const { syncDeletedSubaccountsPubkey } = useSyncDeletedSubaccountsPubkey();

	const emulateAccount = useCallback(
		async (props: { authority: PublicKey; subAccountPubKey?: PublicKey }) => {
			if (!driftClient || !connection) return;

			setDriftStore((s) => {
				s.isEmulatingAccount = true;
			});

			const newKeypair = new Keypair({
				publicKey: props.authority.toBytes(),
				secretKey: new Keypair().publicKey.toBytes(),
			});

			const newWallet: BaseMessageSignerWalletAdapter = {
				publicKey: newKeypair.publicKey,
				autoApprove: false,
				connected: true,
				//@ts-ignore
				signTransaction: () => {
					return Promise.resolve();
				},
				//@ts-ignore
				signAllTransactions: () => {
					return Promise.resolve();
				},
				//@ts-ignore
				connect: () => {},
				disconnect: async () => {},
				//@ts-ignore
				on: (_event: string, _fn: () => void) => {},
			};
			if (!newWallet.publicKey) return;

			const success = await driftClient.updateWallet(
				newWallet as IWallet,
				undefined,
				undefined,
				true
			);

			if (!success) {
				console.log('Error emulating account');
				return;
			}

			const subscribeSuccess = await driftClient.subscribe();
			if (!subscribeSuccess) {
				driftClient.unsubscribe();
				console.log('Error subscribing to emulated account');
				return;
			}

			const mockUsdcFaucet = new TokenFaucet(
				connection,
				newWallet as IWallet,
				new PublicKey(VELOCITY_PROGRAM_ID),
				new PublicKey(DEFAULT_PUBLIC_KEY.toString())
			);

			setDriftStore((state) => {
				state.faucet = mockUsdcFaucet;
				state.wallet.current = {
					adapter: newWallet,
					readyState: WalletReadyState.Installed,
				};
			});

			const walletConnectionState = walletState.connectionState;

			walletConnectionState.progress('AdapterConnected');
			walletConnectionState.progress('ClientConnected');

			setDriftStore((s) => {
				s.wallet.connectionState = walletConnectionState;
			});

			fetchAndSubscribeSubAccounts(props.subAccountPubKey, true);
			syncDeletedSubaccountsPubkey(newWallet.publicKey);

			walletConnectionState.progress('SubaccountsSubscribed');
		},
		[
			driftClient,
			walletState.connectionState,
			connection,
			setDriftStore,
			fetchAndSubscribeSubAccounts,
			syncDeletedSubaccountsPubkey,
		]
	);

	const emulateAccountForSigning = useCallback(
		async (props: {
			authority: PublicKey;
			subAccountPubKey?: PublicKey;
		}): Promise<boolean> => {
			if (!driftClient) return false;

			setDriftStore((s) => {
				s.isEmulatingAccount = true;
			});

			const success = await driftClient.emulateAccount(props.authority);

			if (!success) {
				console.log('Error emulating account');
				return false;
			}

			resetAccountsState();

			fetchAndSubscribeSubAccounts(props.subAccountPubKey, true);

			setDriftStore((s) => {
				s.authorityParam = props.authority.toString();
			});

			return true;
		},
		[
			driftClient,
			setDriftStore,
			resetAccountsState,
			fetchAndSubscribeSubAccounts,
		]
	);

	return {
		emulateAccount,
		emulateAccountForSigning,
	};
};

export default useEmulateAccount;
